import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import { FiRefreshCw, FiAlertCircle, FiClock, FiExternalLink, FiSave } from "react-icons/fi";
import { usePermission } from "../../../context/PermissionContext";
import {
  fetchEventExchangeRates,
  saveEventExchangeRate,
  deleteEventExchangeRate,
  saveEventExchangeSettings,
} from "../../../utils/exchangeApi";
import { fmt as formatNumber, limitPoints, pointsPerUnitOf } from "../../../utils/exchangeRate";

/**
 * Event Exchange for one event: what each currency costs in this event's
 * points, its limits, and how early exchange closes.
 *
 * Each currency uses its default price from Exchange Currencies unless this
 * event saves its own rate here — which can also add a per-user cap, a budget,
 * KYC, or switch the currency off for this event. "Use default" drops the
 * event's own rate again. A currency with neither is not offered here.
 *
 * A new price applies to orders from now on; orders already made keep their
 * own rate. Exchanged points come off the user's event score, so they lower rank.
 */

const fmt = (n, max = 8) =>
  n === null || n === undefined || n === "" ? "—" : formatNumber(n, max);

// The rate is entered as "rate_points event points = rate_units <coin>"; a saved
// one shows as "<points per 1> event points = 1 <coin>". Min, max and the
// per-user limit are amounts of the coin ("1 PX", "500 USDT").
const toForm = (rate) => ({
  rate_points: rate?.points_per_unit ?? "",
  rate_units: 1,
  min_exchange_units: rate?.min_exchange_units ?? "",
  max_exchange_units: rate?.max_exchange_units ?? "",
  fee_percentage: rate?.fee_percentage ?? 0,
  user_max_units: rate?.user_max_units ?? "",
  budget_units: rate?.budget_units ?? "",
  require_kyc: rate ? !!rate.require_kyc : false,
  is_enabled: rate ? !!rate.is_enabled : true,
});

const orNull = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

const cellInput =
  "w-full px-2 py-1.5 border border-gray-200 rounded-lg text-xs text-right focus:outline-none focus:ring-2 focus:ring-blue-100 disabled:bg-gray-50";

/** What the row says about where its numbers come from. */
const sourceLabel = (option) => {
  if (option.rate && !option.rate.is_enabled) return { text: "Switched off for this event", tone: "text-red-500" };
  if (option.source === "event") return { text: "This event's own price", tone: "text-blue-600" };
  if (option.source === "default") return { text: "Using the currency default", tone: "text-gray-500" };
  return { text: "Not offered in this event", tone: "text-amber-600" };
};

const RateRow = ({ eventId, option, editable, onSaved }) => {
  // What the event uses now: its own rate, else the currency default.
  const base = option.rate || option.default_rate;
  // Seeded once. The parent keys this row by what it was seeded from, so a
  // newer saved rate remounts it — no effect syncing props into state, which
  // flushed late and wiped the admin's first edit.
  const [form, setForm] = useState(() => toForm(base));
  const [saving, setSaving] = useState(false);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const dirty = JSON.stringify(form) !== JSON.stringify(toForm(base));
  const label = sourceLabel(option);

  const useDefault = async () => {
    setSaving(true);
    try {
      const res = await deleteEventExchangeRate(eventId, option.id);
      toast.success(res.message || "Done");
      onSaved();
    } catch (err) {
      toast.error(err.message || "Could not change");
    } finally {
      setSaving(false);
    }
  };

  const symbol = option.symbol_name;
  const perUnit = pointsPerUnitOf(form.rate_points, form.rate_units);
  const limits = limitPoints(perUnit, form.min_exchange_units, form.max_exchange_units, option.decimals);
  const capPoints =
    perUnit && form.user_max_units !== "" ? Math.floor(Number((Number(form.user_max_units) * perUnit).toPrecision(15))) : null;

  const save = async () => {
    if (!perUnit) {
      return toast.error(`Set the ${symbol} rate: how many event points buy how many ${symbol}`);
    }
    setSaving(true);
    try {
      await saveEventExchangeRate(eventId, option.id, {
        points_per_unit: perUnit,
        min_exchange_units: orNull(form.min_exchange_units),
        max_exchange_units: orNull(form.max_exchange_units),
        fee_percentage: Number(form.fee_percentage) || 0,
        user_max_units: orNull(form.user_max_units),
        budget_units: orNull(form.budget_units),
        require_kyc: !!form.require_kyc,
        is_enabled: !!form.is_enabled,
      });
      toast.success(`${option.symbol_name} rate saved`);
      onSaved();
    } catch (err) {
      toast.error(err.message || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const disabled = !editable || saving;

  return (
    <tr className="align-top hover:bg-gray-50/60">
      <td className="px-3 py-3">
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full shrink-0" style={{ background: option.color }} />
          <div>
            <div className="font-bold text-gray-800">{option.symbol_name}</div>
            <div className="text-[10px] text-gray-400">
              {option.is_crypto ? "Crypto · admin sends" : "In-app PX · instant"}
              {!option.currency_enabled && <span className="text-red-500"> · currency off</span>}
            </div>
            <div className={`text-[10px] mt-0.5 ${label.tone}`}>{label.text}</div>
          </div>
        </div>
      </td>
      {/* Rate, top to bottom: "10000 event points" = "1 PX". */}
      <td className="px-2 py-3 w-40">
        <label className="block text-[10px] text-gray-500">Event points</label>
        <input
          type="number"
          min={0}
          step="any"
          disabled={disabled}
          aria-label={`${symbol} event points`}
          value={form.rate_points}
          onChange={(e) => set("rate_points", e.target.value)}
          className={cellInput}
          placeholder="10000"
        />
        <label className="block text-[10px] text-gray-500 mt-1">= {symbol} given</label>
        <input
          type="number"
          min={0}
          step="any"
          disabled={disabled}
          aria-label={`${symbol} given`}
          value={form.rate_units}
          onChange={(e) => set("rate_units", e.target.value)}
          className={cellInput}
          placeholder="1"
        />
        <div className="text-[10px] text-gray-400 mt-1 text-right">
          {perUnit ? `1 ${symbol} = ${fmt(perUnit, 6)} EP` : "Set both"}
        </div>
      </td>
      {/* Limits in the coin itself; the points they cost underneath. */}
      <td className="px-2 py-3 w-28">
        <input type="number" min={0} step="any" disabled={disabled} aria-label={`${symbol} min`} value={form.min_exchange_units} onChange={(e) => set("min_exchange_units", e.target.value)} className={cellInput} placeholder="Smallest" />
        <div className="text-[10px] text-gray-400 mt-1 text-right">
          {limits.minPoints != null ? `${symbol} · = ${fmt(limits.minPoints)} EP` : symbol}
        </div>
      </td>
      <td className="px-2 py-3 w-28">
        <input type="number" min={0} step="any" disabled={disabled} aria-label={`${symbol} max`} value={form.max_exchange_units} onChange={(e) => set("max_exchange_units", e.target.value)} className={cellInput} placeholder="No limit" />
        <div className="text-[10px] text-gray-400 mt-1 text-right">
          {limits.maxPoints != null ? `${symbol} · = ${fmt(limits.maxPoints)} EP` : symbol}
        </div>
      </td>
      <td className="px-2 py-3 w-20">
        <input type="number" min={0} max={99.99} step="any" disabled={disabled} value={form.fee_percentage} onChange={(e) => set("fee_percentage", e.target.value)} className={cellInput} />
      </td>
      <td className="px-2 py-3 w-28">
        <input type="number" min={0} step="any" disabled={disabled} aria-label={`${symbol} per user`} value={form.user_max_units} onChange={(e) => set("user_max_units", e.target.value)} className={cellInput} placeholder="No cap" />
        <div className="text-[10px] text-gray-400 mt-1 text-right">
          {capPoints != null ? `${symbol} · = ${fmt(capPoints)} EP` : symbol}
        </div>
      </td>
      <td className="px-2 py-3 w-28">
        <input type="number" min={0} step="any" disabled={disabled} value={form.budget_units} onChange={(e) => set("budget_units", e.target.value)} className={cellInput} placeholder="No budget" />
        {option.usage.budget_remaining !== null && (
          <div className="text-[10px] text-gray-400 mt-1 text-right">{fmt(option.usage.budget_remaining)} left</div>
        )}
      </td>
      <td className="px-2 py-3 text-center">
        <input type="checkbox" disabled={disabled} checked={form.require_kyc} onChange={(e) => set("require_kyc", e.target.checked)} />
      </td>
      <td className="px-2 py-3 text-center">
        <input type="checkbox" disabled={disabled} checked={form.is_enabled} onChange={(e) => set("is_enabled", e.target.checked)} />
      </td>
      <td className="px-3 py-3 text-right text-[11px] text-gray-600 whitespace-nowrap">
        <div>{fmt(option.usage.orders)} orders{option.usage.pending ? ` · ${option.usage.pending} pending` : ""}</div>
        <div className="text-gray-400">{fmt(option.usage.points_spent)} EP</div>
        <div className="text-gray-400">{fmt(option.usage.tokens_committed)} {option.symbol_name}</div>
      </td>
      {editable && (
        <td className="px-3 py-3 text-right whitespace-nowrap">
          <button
            onClick={save}
            disabled={saving || !dirty}
            className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-semibold disabled:opacity-30 inline-flex items-center gap-1"
          >
            <FiSave size={12} /> {saving ? "…" : "Save"}
          </button>
          {option.rate && (
            <button
              onClick={useDefault}
              disabled={saving}
              title={option.default_rate ? "Drop this event's own rate and use the currency default" : "Drop this event's rate; the currency has no default, so it will not be offered here"}
              className="block mt-1.5 ml-auto text-[10px] font-semibold text-gray-500 hover:text-blue-600 disabled:opacity-30"
            >
              {option.default_rate ? "Use default" : "Remove"}
            </button>
          )}
        </td>
      )}
    </tr>
  );
};

const ExchangeRatesTab = ({ eventId }) => {
  const { hasPermission } = usePermission();
  const canEdit = hasPermission("exchange", "edit");

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [closeHours, setCloseHours] = useState("");
  const [savingHours, setSavingHours] = useState(false);

  const load = useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetchEventExchangeRates(eventId);
      setData(res.data);
      setCloseHours(String(res.data?.event?.exchange_close_hours ?? ""));
    } catch (err) {
      setError(err.message || "Could not load exchange rates");
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    load();
  }, [load]);

  const saveHours = async () => {
    setSavingHours(true);
    try {
      await saveEventExchangeSettings(eventId, { exchange_close_hours: Number(closeHours) || 0 });
      toast.success("Exchange close time saved");
      load();
    } catch (err) {
      toast.error(err.message || "Could not save");
    } finally {
      setSavingHours(false);
    }
  };

  if (error) {
    return (
      <div className="p-6">
        <div className="p-4 rounded-xl bg-red-50 border border-red-100 text-xs text-red-700 flex items-center gap-2">
          <FiAlertCircle /> {error}
        </div>
      </div>
    );
  }

  const event = data?.event;
  // Editable whatever the event's status — finished events included.
  const editable = canEdit && !!event;

  return (
    <div className="p-6 space-y-5">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
        <div className="text-xs text-gray-500 max-w-2xl space-y-1">
          <p>
            Users exchange this event's points for the currencies priced below. Exchanged points come off their event
            score, so they lose rank. PX is credited instantly; crypto waits for an admin in{" "}
            <Link to={`/exchange-orders?event=${eventId}`} className="text-blue-600 font-medium inline-flex items-center gap-0.5">
              Exchange Orders <FiExternalLink size={11} />
            </Link>
            .
          </p>
          <p className="text-gray-400">
            Each currency starts at its default from{" "}
            <Link to="/exchange-currencies" className="text-blue-600 font-medium">
              Exchange Currencies
            </Link>
            . Saving a row gives this event its own price and limits; "Use default" goes back.
          </p>
        </div>

        <div className="flex items-end gap-2">
          <label className="block">
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Close before end (hours)</span>
            <input
              type="number"
              min={0}
              max={720}
              step="any"
              disabled={!editable || savingHours}
              value={closeHours}
              onChange={(e) => setCloseHours(e.target.value)}
              className="mt-1 w-28 px-3 py-2 border border-gray-200 rounded-xl text-sm disabled:bg-gray-50"
            />
          </label>
          {editable && (
            <button
              onClick={saveHours}
              disabled={savingHours || String(event?.exchange_close_hours) === closeHours}
              className="px-3 py-2 rounded-xl bg-blue-600 text-white text-xs font-semibold disabled:opacity-30"
            >
              Save
            </button>
          )}
          <button onClick={load} className="p-2 border border-gray-200 rounded-xl text-gray-500 hover:bg-gray-50" title="Refresh">
            <FiRefreshCw size={14} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {event && (
        <div
          className={`p-3 rounded-xl border text-xs flex items-center gap-2 ${
            event.is_open ? "bg-emerald-50 border-emerald-100 text-emerald-800" : "bg-gray-50 border-gray-200 text-gray-600"
          }`}
        >
          <FiClock />
          {event.is_open ? (
            <span>
              Exchange is <strong>open</strong> until {event.closes_at ? new Date(event.closes_at).toLocaleString() : "the end"}.
            </span>
          ) : (
            <span>
              Exchange is <strong>closed</strong>: {event.closed_reason}
              {event.closes_at ? ` (closes ${new Date(event.closes_at).toLocaleString()})` : ""}.
            </span>
          )}
        </div>
      )}

      <div className="overflow-x-auto border border-gray-100 rounded-2xl">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 text-gray-500 uppercase text-[10px] tracking-wider">
            <tr>
              <th className="text-left px-3 py-3">Currency</th>
              <th className="text-left px-2 py-3">Exchange rate</th>
              <th className="text-right px-2 py-3">Min per exchange</th>
              <th className="text-right px-2 py-3">Max per exchange</th>
              <th className="text-right px-2 py-3">Fee %</th>
              <th className="text-right px-2 py-3">Per-user limit</th>
              <th className="text-right px-2 py-3">Budget</th>
              <th className="text-center px-2 py-3">KYC</th>
              <th className="text-center px-2 py-3">On</th>
              <th className="text-right px-3 py-3">Used</th>
              {editable && <th className="px-3 py-3" />}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {data?.options?.length === 0 && (
              <tr>
                <td colSpan={11} className="px-4 py-10 text-center text-gray-400">
                  No currencies yet — add them under Exchange Currencies.
                </td>
              </tr>
            )}
            {(data?.options || []).map((option) => (
              <RateRow
                key={`${option.id}:${option.rate?.updated_at || "none"}:${JSON.stringify(option.default_rate || null)}`}
                eventId={eventId}
                option={option}
                editable={editable}
                onSaved={load}
              />
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[10px] text-gray-400">
        Min, max, the per-user limit and the budget are amounts of the currency itself (e.g. 1 PX, 500 USDT); the event points they need are shown underneath. Leave a field empty for no limit.
      </p>
    </div>
  );
};

export default ExchangeRatesTab;
